import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import { useDispatch, useSelector } from 'react-redux';

import SvgIcon from 'components/SvgIcon/SvgIcon';
import DisplaynameSection from './components/DisplaynameSection';
import HireButton from './components/Buttons/HireButton';
import ShortlistButton from './components/Buttons/ShortlistButton';
import ResumeUpload from 'containers/Onboarding/ResumeUpload';
import Loader from 'components/Loader/Loader';
import { uploadResume } from 'redux/modules/auth';
import styles from './PortfolioHeader.module.scss';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from 'react-bootstrap';
import { loadUser } from 'redux/modules/portfolio';
import { trackEvent } from 'helpers/Analytics';
import { setSuccessMessage } from 'redux/modules/successMessage';
import {
  getCoverPresetById,
  getCoverSource,
} from 'helpers/profileCoverPresets';
import {
  Camera,
  Eye,
  ImagePlus,
  LayoutGrid,
  MapPin,
  Star,
  Upload,
} from 'lucide-react';
import { getBioSnippet, getInitials } from 'helpers/portfolioLayout';

const { carouselButton, previous, chevron, displayname, carouselAvatar, next } =
  styles;

function isPlaceholderAvatar(avatars) {
  if (!avatars) {
    return true;
  }
  if (avatars.temp) {
    return false;
  }
  if (avatars.placeholder) {
    return true;
  }
  return false;
}

// No cover uploaded: a flat navy band. A preset the owner just picked paints
// straight away while its image renders and uploads.
const NO_COVER_BACKGROUND = '#04121F';

function resolveCoverCss(user) {
  if (!user) {
    return NO_COVER_BACKGROUND;
  }
  const source = getCoverSource(user.id);
  if (source.type === 'preset' && source.presetId) {
    return getCoverPresetById(source.presetId).css;
  }
  return NO_COVER_BACKGROUND;
}

const PortfolioHeader = ({
  existingBriefs,
  loggedInUser,
  nextUser,
  prevUser,
  shortlistState,
  toggleShortlist,
  onAccept,
  onHire,
  onHireByDay,
  onRatings,
  onUpdateAvatar,
  onUpdateCover,
  pitches,
  user,
  isLoadingOwnBriefs,
  locationFindUrl,
  narrow,
  projectCount: shownProjectCount,
  previewAsClient,
  onPreviewAsClient,
}) => {
  const dispatch = useDispatch();

  const {
    uploadingResume,
    uploadedResume,
    uploadResumeError,
    uploadResumeErrorDetails,
    uploadResumeSuccess,
  } = useSelector((state) => state.auth);

  const [showResumeUploader, setShowResumeUploader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedResumeFile, setSelectedResumeFile] = useState(null);

  useEffect(() => {
    if (uploadedResume && uploadResumeError && !uploadingResume) {
      trackEvent('user-uploads-resume-portfolio-error', {
        error_message: uploadResumeError,
        error_details:
          uploadResumeErrorDetails || 'No detailed error information available',
      });
      setUploadError(uploadResumeError);
      setUploading(false);
    }
  }, [uploadResumeError, uploadedResume, uploadingResume]);

  useEffect(() => {
    if (uploadedResume && uploadResumeSuccess && !uploadingResume) {
      trackEvent('user-uploads-resume-portfolio-success');
      setUploadSuccess(true);
      setUploading(false);
      dispatch(
        setSuccessMessage('Resume processed and portfolio updated successfully')
      );
      dispatch(loadUser(user.username)).then(() => {
        setShowResumeUploader(false);
        setUploadError(null);
        setUploadSuccess(false);
        setSelectedResumeFile(null);
      });
    }
  }, [uploadedResume, uploadingResume]);

  const handleResumeFileSelect = (file) => {
    if (!file) return;
    setSelectedResumeFile(file);
    setUploadError(null);
  };

  const handleResumeUpload = () => {
    if (!selectedResumeFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    trackEvent('user-uploads-resume-portfolio', {
      fileExtension: selectedResumeFile.name.split('.').pop().toLowerCase(),
    });
    dispatch(uploadResume(selectedResumeFile));
  };

  // Only render if user is loaded
  if (!user) {
    return null;
  }

  const isOwner = !!(loggedInUser && user && loggedInUser.id === user.id);
  // Previewing renders the public header; its client actions do nothing.
  const currentUser = isOwner && !previewAsClient;
  const noop = () => {};
  const showBio = user.bio && (user.activated || currentUser);
  const bioSnippet = showBio ? getBioSnippet(user.bio) : '';
  const hasAvatar = !isPlaceholderAvatar(user.avatars);
  const hasCover = !(user.covers?.placeholder && !user.covers?.temp);
  const locationText =
    user.location.text && user.location.text !== 'Private'
      ? user.location.text
      : '';
  const projectCount = shownProjectCount || 0;
  const reviewCount = user.rating?.count || 0;

  const hireButton = !currentUser && (
    <HireButton
      onAccept={previewAsClient ? noop : onAccept}
      currentUser={currentUser}
      existingBriefs={previewAsClient ? [] : existingBriefs}
      onHire={previewAsClient ? noop : onHire}
      onHireByDay={previewAsClient ? noop : onHireByDay}
      loggedInUser={loggedInUser}
      pitches={previewAsClient ? null : pitches}
      user={user}
      isLoadingOwnBriefs={!previewAsClient && isLoadingOwnBriefs}
    />
  );

  const landingBanner = (nextUser || prevUser) && (
    <div className={styles.landingBanner}>
      {prevUser && (
        <Link
          to={prevUser.links.main_relative}
          className={`${carouselButton} ${previous}`}
        >
          <SvgIcon
            className={chevron}
            icon="chevron-left"
            size="16"
            color="#fff"
          />
          <p className={displayname}>{prevUser.displayname}</p>
          <Thumbnail
            thumbnails={prevUser.avatars}
            className={carouselAvatar}
            circle
          />
        </Link>
      )}
      {nextUser && (
        <Link
          to={nextUser.links.main_relative}
          className={`${carouselButton} ${next}`}
        >
          <Thumbnail
            thumbnails={nextUser.avatars}
            circle
            className={carouselAvatar}
          />
          <p className={displayname}>{nextUser.displayname}</p>
          <SvgIcon
            className={chevron}
            icon="chevron-right"
            size="16"
            color="#fff"
          />
        </Link>
      )}
    </div>
  );

  const avatarImage = hasAvatar ? (
    <Thumbnail
      thumbnails={user.avatars}
      className={styles.avatarImage}
      id="portfolio-avatar"
      circle
    />
  ) : (
    <span
      className={`${styles.avatarImage} ${styles.initials}`}
      id="portfolio-avatar"
      aria-hidden={currentUser ? 'true' : undefined}
      role={currentUser ? undefined : 'img'}
      aria-label={currentUser ? undefined : user.displayname}
    >
      {getInitials(user.displayname)}
    </span>
  );

  const ratingAvg = reviewCount > 0 && user.rating.avg;

  return (
    <div className={styles.portfolioHeader}>
      {landingBanner}
      <div
        id="portfolio-cover"
        className={styles.cover}
        style={hasCover ? undefined : { background: resolveCoverCss(user) }}
      >
        {hasCover && (
          <Thumbnail
            className={styles.coverImage}
            thumbnails={user.covers}
            style={{ cursor: currentUser ? 'pointer' : 'initial' }}
            onClick={currentUser && onUpdateCover}
          />
        )}
        {currentUser ? (
          <button
            type="button"
            className={styles.editCoverButton}
            onClick={onUpdateCover}
            aria-label="Change cover photo"
          >
            <ImagePlus size={16} strokeWidth={2} aria-hidden="true" />
            <span>Edit cover</span>
          </button>
        ) : null}
      </div>
      <div className={`${styles.container} ${narrow ? styles.narrow : ''}`}>
        <section className={styles.card} aria-label="Profile header">
          <div className={styles.avatar}>
            {currentUser ? (
              <button
                type="button"
                className={styles.avatarButton}
                onClick={onUpdateAvatar}
                aria-label={
                  hasAvatar
                    ? 'Change profile picture'
                    : 'Upload a profile picture'
                }
              >
                {avatarImage}
                <span className={styles.avatarBadge} aria-hidden="true">
                  <Camera size={18} strokeWidth={2} />
                </span>
              </button>
            ) : (
              avatarImage
            )}
          </div>
          <div className={styles.main}>
            <DisplaynameSection
              isOwner={currentUser ? true : false}
              user={user}
            />
            {(locationText || ratingAvg || projectCount > 0) && (
              <ul className={styles.meta}>
                {locationText && (
                  <li>
                    <MapPin size={16} strokeWidth={2} aria-hidden="true" />
                    {!currentUser && locationFindUrl ? (
                      <a href={locationFindUrl} className={styles.metaLink}>
                        {locationText}
                      </a>
                    ) : (
                      locationText
                    )}
                  </li>
                )}
                {ratingAvg ? (
                  <li>
                    <Star
                      size={16}
                      strokeWidth={2}
                      fill="#FFB400"
                      color="#FFB400"
                      aria-hidden="true"
                    />
                    <strong className={styles.ratingValue}>
                      {user.rating.avg.toFixed(1)}
                    </strong>
                    <button
                      type="button"
                      className={styles.reviewsLink}
                      onClick={onRatings}
                    >
                      {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                    </button>
                  </li>
                ) : null}
                {projectCount > 0 && (
                  <li>
                    <LayoutGrid size={16} strokeWidth={2} aria-hidden="true" />
                    {projectCount} {projectCount === 1 ? 'project' : 'projects'}
                  </li>
                )}
              </ul>
            )}
            {bioSnippet && <p className={styles.bioSnippet}>{bioSnippet}</p>}
          </div>
          <div
            className={`${styles.actions} ${
              currentUser ? styles.ownerActions : ''
            }`}
          >
            {currentUser && (
              <button
                type="button"
                onClick={() => setShowResumeUploader(true)}
                className={styles.uploadButton}
              >
                <Upload size={16} strokeWidth={2} aria-hidden="true" />
                Upload resume
              </button>
            )}
            {currentUser && onPreviewAsClient && (
              <button
                type="button"
                onClick={onPreviewAsClient}
                className={styles.previewButton}
              >
                <Eye size={16} strokeWidth={2} aria-hidden="true" />
                View as a client
              </button>
            )}
            {hireButton}
            {loggedInUser && !currentUser && (
              <ShortlistButton
                shortlistState={!previewAsClient && shortlistState}
                toggleShortlist={previewAsClient ? noop : toggleShortlist}
                disabled={!!previewAsClient}
                name={user.displayname}
              />
            )}
          </div>
        </section>
      </div>
      <Modal
        show={showResumeUploader}
        onHide={() => {
          if (!uploading) {
            setShowResumeUploader(false);
            setUploadError(null);
            setUploadSuccess(false);
            setSelectedResumeFile(null);
          }
        }}
      >
        <ModalHeader closeButton={!uploading}>
          <ModalTitle className="font-bold">Upload resume</ModalTitle>
        </ModalHeader>
        <ModalBody bsClass={styles.resumeModal}>
          {uploading && (
            <div className={styles.loadingOverlay}>
              <Loader loading />
              <span className={styles.loadingOverlayText}>
                Uploading your resume, hang tight...
              </span>
            </div>
          )}
          <div className={styles.resumeWarning}>
            <SvgIcon
              className={styles.resumeWarningIcon}
              icon="warning"
              size="32"
            />
            <div>
              <p className={styles.resumeWarningText}>
                <strong>Warning:</strong>{' '}
                {`Uploading will overwrite your bio with content from your resume. You can't recover your previous bio, but can edit your bio at any time.`}
              </p>
            </div>
          </div>
          <ResumeUpload
            onUpload={handleResumeFileSelect}
            uploading={uploading}
            uploadError={uploadError}
            uploadSuccess={uploadSuccess}
            showReupload={false}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              if (!uploading) {
                setShowResumeUploader(false);
                setUploadError(null);
                setUploadSuccess(false);
                setSelectedResumeFile(null);
              }
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleResumeUpload}
            disabled={!selectedResumeFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

PortfolioHeader.propTypes = {
  editMode: PropTypes.bool,
  editDisplayname: PropTypes.string,
  existingBriefs: PropTypes.array,
  loggedInUser: PropTypes.object,
  nextUser: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
  prevUser: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
  shortlistState: PropTypes.bool,
  toggleShortlist: PropTypes.func,
  verificationState: PropTypes.bool,
  onAccept: PropTypes.func,
  onHire: PropTypes.func,
  onVerify: PropTypes.func,
  onHireByDay: PropTypes.func,
  onRatings: PropTypes.func,
  onUpdateDisplayname: PropTypes.func,
  uploadResume: PropTypes.func,
  onUpdateRoles: PropTypes.func,
  onUpdateAvatar: PropTypes.func,
  onUpdateCover: PropTypes.func,
  pitches: PropTypes.oneOf([PropTypes.array, PropTypes.bool]),
  user: PropTypes.object,
  isLoadingOwnBriefs: PropTypes.bool,
  locationFindUrl: PropTypes.string,
  narrow: PropTypes.bool,
  projectCount: PropTypes.number,
  previewAsClient: PropTypes.bool,
  onPreviewAsClient: PropTypes.func,
};

export default PortfolioHeader;
